import { ActivityType } from "@/types/activity"
import { Loader2, Save, AlarmClock, Calendar, FileText } from "lucide-react"
import { formatDateToUTC, formatDbTimestampToLocal } from "@/lib/utils/date"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { format } from "date-fns"
import { cn } from "@/lib/utils"
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Card,
  CardContent
} from "@/components/ui/card"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"

// Import our custom types
import { ActivityEditDialogProps } from "@/types/activities/activityEdit"
import { useActivityEditForm } from "./hooks/useActivityEditForm"
import { useActivityLookupData } from "./hooks/useActivityLookupData"

// Updated interface to support both versions of props
type EnhancedActivityEditDialogProps = ActivityEditDialogProps | {
  activity: ActivityType;
  type: 'sleep' | 'nap' | 'feeding';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  childId?: string;
  onSuccess?: () => void;
};

export function ActivityEditDialog(props: EnhancedActivityEditDialogProps) {
  // Normalize props to match what our components expect
  const activity = 'activity' in props ? props.activity : null;
  const activityType = 'activityType' in props ? props.activityType : 
                      ('type' in props ? props.type : 'sleep');
  const isOpen = 'isOpen' in props ? props.isOpen : 
               ('open' in props ? props.open : false);
  
  // Create a combined onClose handler
  const handleClose = () => {
    if ('onClose' in props && props.onClose) {
      props.onClose();
    } else if ('onOpenChange' in props && props.onOpenChange) {
      props.onOpenChange(false);
      if ('onSuccess' in props && props.onSuccess) {
        props.onSuccess();
      }
    }
  };
  
  // Use our custom form hook for managing state and logic
  const {
    formState,
    setField,
    calculateDuration,
    handleSave
  } = useActivityEditForm(activity, activityType, handleClose);
  
  // Use our custom hook for lookup data only for sleep and nap activities
  // This fixes the TypeScript error by ensuring we only pass 'sleep' or 'nap' to the hook
  const lookupData = useActivityLookupData(
    activityType === 'sleep' || activityType === 'nap' 
      ? activityType 
      : 'sleep' // Default to sleep for any other activity type
  );
  
  // If no activity is selected, don't render the dialog content
  if (!activity) {
    return null;
  }
  
  // Extract form state for cleaner code
  const {
    notes,
    startDate,
    startTime,
    endDate,
    endTime,
    timeValid,
    locationId,
    environmentId,
    onsetMethodId,
    restfulnessId,
    signsOfSleepDebt,
    soundId,
    soundLevelId,
    isSubmitting
  } = formState;
  
  // Get suggestion lists based on activity type
  const getSuggestions = () => {
    if (activityType === 'sleep') {
      return [
        "Sleep cues observed",
        "Any disruptions during sleep",
        "Mood before and after sleep",
        "Changes to normal routine",
        "Environment factors that might have affected sleep",
        "Position baby slept in"
      ];
    } else if (activityType === 'nap') {
      return [
        "Drowsy signs observed before nap",
        "Length of awake window before nap",
        "Quality of nap",
        "Any unusual interruptions",
        "Mood upon waking",
        "Settling methods that worked"
      ];
    } else if (activityType === 'feeding') {
      return [
        "Amount consumed",
        "Feeding position",
        "Any discomfort or reflux",
        "Distractions during feeding",
        "Hunger cues observed",
        "Burping results"
      ];
    }
    return [];
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit {activityType.charAt(0).toUpperCase() + activityType.slice(1)} Record</DialogTitle>
          <DialogDescription>
            Make changes to the {activityType} record and save when done.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto px-1">
        <Accordion type="multiple" defaultValue={["datetime", "details"]} className="space-y-3">
          {/* Date & Time Section */}
          <AccordionItem value="datetime" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline py-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span className="font-medium">Date & Time</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 pb-4">
          {/* Start Date/Time */}
          <div className="space-y-2">
            <Label htmlFor="startDate">Start Date</Label>
            <div className="flex space-x-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="startDate"
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => setField('startDate', date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="startTime">Start Time</Label>
            <div className="flex items-center space-x-2">
              <AlarmClock className="h-4 w-4 text-muted-foreground" />
              <Input
                id="startTime"
                type="time"
                value={startTime}
                onChange={(e) => setField('startTime', e.target.value)}
                className="flex-1"
              />
            </div>
          </div>
          
          {/* End Date/Time */}
          <div className="space-y-2">
            <Label htmlFor="endDate">End Date</Label>
            <div className="flex space-x-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="endDate"
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => setField('endDate', date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="endTime">End Time</Label>
            <div className="flex items-center space-x-2">
              <AlarmClock className="h-4 w-4 text-muted-foreground" />
              <Input
                id="endTime"
                type="time"
                value={endTime}
                onChange={(e) => setField('endTime', e.target.value)}
                className="flex-1"
              />
            </div>
          </div>
          
              {/* Duration Display */}
              <div className="col-span-1 md:col-span-2 space-y-1">
                <p className="text-sm text-muted-foreground">
                  Duration: {calculateDuration() || 'Invalid time selection'}
                </p>
                {!timeValid && (
                  <p className="text-sm text-destructive">
                    End time must be after start time
                  </p>
                )}
              </div>
              </div>
            </AccordionContent>
          </AccordionItem>
          
          {/* SLEEP SPECIFIC FIELDS */}
          {activityType === 'sleep' && (
            <AccordionItem value="details" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline py-3">
                <span className="font-medium">Sleep Details</span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 pb-4">
              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Select 
                  value={locationId} 
                  onValueChange={(value) => setField('locationId', value)}
                >
                  <SelectTrigger id="location">
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {lookupData.locations.map(location => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Environment */}
              <div className="space-y-2">
                <Label htmlFor="environment">Environment</Label>
                <Select 
                  value={environmentId} 
                  onValueChange={(value) => setField('environmentId', value)}
                >
                  <SelectTrigger id="environment">
                    <SelectValue placeholder="Select an environment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {lookupData.environments.map(env => (
                      <SelectItem key={env.id} value={env.id}>
                        {env.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Onset Method */}
              <div className="space-y-2">
                <Label htmlFor="onsetMethod">How did the baby fall asleep?</Label>
                <Select 
                  value={onsetMethodId} 
                  onValueChange={(value) => setField('onsetMethodId', value)}
                >
                  <SelectTrigger id="onsetMethod">
                    <SelectValue placeholder="Select a method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {lookupData.onsetMethods.map(method => (
                      <SelectItem key={method.id} value={method.id}>
                        {method.method}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Sound */}
              <div className="space-y-2">
                <Label htmlFor="sound">Sound during sleep</Label>
                <Select 
                  value={soundId} 
                  onValueChange={(value) => setField('soundId', value)}
                >
                  <SelectTrigger id="sound">
                    <SelectValue placeholder="Select a sound" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {lookupData.sounds.map(sound => (
                      <SelectItem key={sound.id} value={sound.id}>
                        {sound.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Sound Level */}
              <div className="space-y-2">
                <Label htmlFor="soundLevel">Sound level</Label>
                <Select 
                  value={soundLevelId} 
                  onValueChange={(value) => setField('soundLevelId', value)}
                >
                  <SelectTrigger id="soundLevel">
                    <SelectValue placeholder="Select a sound level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {lookupData.soundLevels.map(level => (
                      <SelectItem key={level.id} value={level.id}>
                        {level.level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Restfulness */}
              <div className="space-y-2">
                <Label htmlFor="restfulness">How restful was the sleep?</Label>
                <Select 
                  value={restfulnessId} 
                  onValueChange={(value) => setField('restfulnessId', value)}
                >
                  <SelectTrigger id="restfulness">
                    <SelectValue placeholder="Select a rating" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {lookupData.restfulnessRatings.map(rating => (
                      <SelectItem key={rating.id} value={rating.id}>
                        {rating.rating}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              </div>
            </AccordionContent>
          </AccordionItem>
          )}
          
          {/* NAP SPECIFIC FIELDS */}
          {activityType === 'nap' && (
            <AccordionItem value="details" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline py-3">
                <span className="font-medium">Nap Details</span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 pb-4">
              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="napLocation">Location</Label>
                <Select 
                  value={locationId} 
                  onValueChange={(value) => setField('locationId', value)}
                >
                  <SelectTrigger id="napLocation">
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {lookupData.napLocations.map(location => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Environment */}
              <div className="space-y-2">
                <Label htmlFor="napEnvironment">Environment</Label>
                <Select 
                  value={environmentId} 
                  onValueChange={(value) => setField('environmentId', value)}
                >
                  <SelectTrigger id="napEnvironment">
                    <SelectValue placeholder="Select an environment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {lookupData.napEnvironments.map(env => (
                      <SelectItem key={env.id} value={env.id}>
                        {env.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Onset Method */}
              <div className="space-y-2">
                <Label htmlFor="napOnsetMethod">How did the baby fall asleep?</Label>
                <Select 
                  value={onsetMethodId} 
                  onValueChange={(value) => setField('onsetMethodId', value)}
                >
                  <SelectTrigger id="napOnsetMethod">
                    <SelectValue placeholder="Select a method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {lookupData.napOnsetMethods.map(method => (
                      <SelectItem key={method.id} value={method.id}>
                        {method.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Restfulness */}
              <div className="space-y-2">
                <Label htmlFor="napRestfulness">How restful was the nap?</Label>
                <Select 
                  value={restfulnessId} 
                  onValueChange={(value) => setField('restfulnessId', value)}
                >
                  <SelectTrigger id="napRestfulness">
                    <SelectValue placeholder="Select a rating" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {lookupData.napRestfulnessRatings.map(rating => (
                      <SelectItem key={rating.id} value={rating.id}>
                        {rating.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              </div>
            </AccordionContent>
          </AccordionItem>
          )}
          
          {/* FEEDING SPECIFIC FIELDS */}
          {activityType === 'feeding' && (
            <AccordionItem value="datetime" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span className="font-medium">Feeding Time</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 pb-4">
                  {/* Start Date/Time for Feeding */}
                  <div className="space-y-2">
                    <Label htmlFor="feedingStartDate">Date</Label>
                    <div className="flex space-x-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            id="feedingStartDate"
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !startDate && "text-muted-foreground"
                            )}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {startDate ? format(startDate, "PPP") : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <CalendarComponent
                            mode="single"
                            selected={startDate}
                            onSelect={(date) => setField('startDate', date)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="feedingStartTime">Time</Label>
                    <div className="flex items-center space-x-2">
                      <AlarmClock className="h-4 w-4 text-muted-foreground" />
                      <Input
                        id="feedingStartTime"
                        type="time"
                        value={startTime}
                        onChange={(e) => setField('startTime', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
          
          {/* COMMON FIELDS FOR ALL ACTIVITY TYPES */}
          {/* Notes Section */}
          <AccordionItem value="notes" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline py-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span className="font-medium">Notes</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pt-2 pb-4">
              {/* Signs of Sleep Debt Checkbox - for both sleep and nap */}
              {(activityType === 'sleep' || activityType === 'nap') && (
                <div className="flex items-center space-x-2">
                  <Checkbox 
                id="signs-of-sleep-debt" 
                checked={signsOfSleepDebt}
                onCheckedChange={(checked) => 
                  setField('signsOfSleepDebt', checked as boolean)
                }
              />
              <label
                htmlFor="signs-of-sleep-debt"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                  Signs of Sleep Debt
                </label>
              </div>
              )}
              
              {/* Notes - for all activity types */}
              <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="notes" className="font-medium">Notes</Label>
            </div>
            
            <Card className="border border-input">
              <CardContent className="p-4 space-y-4">
                <Textarea
                  id="notes"
                  placeholder={`Add any notes about this ${activityType} session...`}
                  className="min-h-[120px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 shadow-none resize-y"
                  value={notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  onKeyDown={(e) => {
                    // Prevent form submission on Enter key
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                    }
                  }}
                />
                
                <div className="text-xs text-muted-foreground space-y-2">
                  <p>Press Shift + Enter for new line</p>
                  
                  <div className="pt-2 border-t border-border">
                    <p className="font-medium">Suggested topics:</p>
                    <ul className="mt-1 space-y-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                      {getSuggestions().map((suggestion, index) => (
                        <li key={index} className="flex items-start">
                          <span className="mr-1.5">•</span>
                          <span>{suggestion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="pt-2 border-t border-border mt-2 flex items-center gap-1">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="h-6 text-xs rounded-sm px-2"
                      onClick={() => {
                        const template = getSuggestions().map(s => `• ${s}:\n`).join('');
                        setField('notes', template);
                      }}
                    >
                      Use Template
                    </Button>
                    <p className="text-xs">Click to insert a formatted template with these topics</p>
                  </div>
                </div>
              </CardContent>
            </Card>
              </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        </div>
        
        {/* Dialog Footer with Actions */}
        <DialogFooter className="flex justify-between">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={isSubmitting || !timeValid}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
